<template>
  <section class="online-appointments" aria-labelledby="online-appointments-title">
    <header class="oa-header">
      <div>
        <p class="oa-eyebrow">Arbeitsplanung</p>
        <h1 id="online-appointments-title">Online Termine</h1>
        <p class="oa-subtitle">Neue Buchungen im Blick. Mit einem Klick zum Termin im Kalender.</p>
      </div>
      <button type="button" class="oa-refresh" :disabled="loading || Boolean(rangeError) || Boolean(openingAppointment)" @click="load()">
        <v-icon size="18" color="currentColor">mdi-refresh</v-icon>
        {{ loading ? 'Wird geladen …' : 'Aktualisieren' }}
      </button>
    </header>

    <div class="oa-filters">
      <div class="oa-filter-heading">
        <span><v-icon size="17" color="currentColor">mdi-filter-variant</v-icon> Buchungseingang</span>
        <div class="oa-presets" aria-label="Zeitraum auswählen">
          <button v-for="preset in presets" :key="preset.days" type="button" :aria-pressed="isPreset(preset.days)" :disabled="Boolean(openingAppointment)" @click="selectPreset(preset.days)">{{ preset.label }}</button>
        </div>
      </div>
      <div class="oa-filter-grid">
        <label class="oa-field oa-field--studio">
          <span>Studio</span>
          <select v-model="studioId" :disabled="Boolean(openingAppointment)" @change="applyFilters">
            <option v-for="studio in studioOptions" :key="studio.id" :value="studio.id">{{ studio.name }}</option>
          </select>
        </label>
        <label class="oa-field">
          <span>Eingang vom</span>
          <input v-model="from" type="date" :max="to || undefined" :disabled="Boolean(openingAppointment)" @change="applyFilters" />
        </label>
        <label class="oa-field">
          <span>Bis</span>
          <input v-model="to" type="date" :min="from || undefined" :disabled="Boolean(openingAppointment)" @change="applyFilters" />
        </label>
      </div>
      <p v-if="rangeError" class="oa-error" role="alert">{{ rangeError }}</p>
    </div>

    <div v-if="error || appointmentError" class="oa-notice" role="alert">
      <v-icon size="20" color="currentColor">mdi-alert-circle-outline</v-icon>
      <span>{{ error || appointmentError }}</span>
    </div>

    <div class="oa-results" :aria-busy="loading">
      <div class="oa-results-heading">
        <div class="oa-results-title">
          <h2>Buchungen</h2>
          <span v-if="!loading && !error && !rangeError" class="oa-count" aria-live="polite">{{ total }}</span>
        </div>
        <span class="oa-order"><v-icon size="16" color="currentColor">mdi-sort-clock-descending-outline</v-icon> Neueste Eingänge zuerst</span>
      </div>
      <div v-if="loading" class="oa-empty" role="status">
        <v-progress-circular indeterminate color="#73dbac" size="28" width="2" />
        <p>Online-Termine werden geladen …</p>
      </div>
      <div v-else-if="!items.length" class="oa-empty">
        <v-icon size="36" color="#91a29c">mdi-calendar-search</v-icon>
        <p>{{ emptyText }}</p>
      </div>
      <table v-else class="oa-table">
        <caption class="oa-sr-only">Online-Buchungen nach Eingang, neueste zuerst. Terminlinks öffnen den Kalender in einem neuen Tab.</caption>
        <thead>
          <tr><th scope="col">Termin</th><th scope="col">Kunde / Gast</th><th scope="col">Studio &amp; Mitarbeiter</th><th scope="col">Eingegangen</th><th scope="col">Status</th><th scope="col"><span class="oa-sr-only">Kalender</span></th></tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.id" class="oa-row" :class="{ 'oa-row--clickable': canOpenAppointment(item), 'oa-row--deleted': item.status === 'deleted' }" @click="openAppointment(item)">
            <td class="oa-cell-appointment">
              <button type="button" class="oa-appointment" :disabled="!canOpenAppointment(item) || Boolean(openingAppointment)" :aria-label="appointmentLinkLabel(item)" @click.stop="openAppointment(item)">
                <span class="oa-date-tile" aria-hidden="true"><strong>{{ datePart(item.date, 'DD') }}</strong><span>{{ datePart(item.date, 'MMM') }}</span></span>
                <span class="oa-appointment-copy">
                  <strong>{{ item.title || 'Termin' }}</strong>
                  <span>{{ datePart(item.date, 'dd, DD.MM.YYYY') }}</span>
                  <span class="oa-time">{{ item.start }}–{{ item.end }} Uhr</span>
                </span>
              </button>
              <span v-if="item.status === 'deleted'" class="oa-deleted-hint">Nicht mehr im Kalender</span>
            </td>
            <td class="oa-cell-person">
              <strong class="oa-person-name">{{ item.customerName }}</strong>
              <span v-if="item.phone" class="oa-detail">{{ item.phone }}</span>
              <span v-if="item.email" class="oa-detail oa-email">{{ item.email }}</span>
            </td>
            <td class="oa-cell-studio">
              <span class="oa-studio-name"><v-icon size="14" color="currentColor">mdi-map-marker-outline</v-icon>{{ item.studioName }}</span>
              <span class="oa-detail">{{ item.employeeName }}</span>
            </td>
            <td class="oa-cell-received"><span>{{ receivedPart(item.createdAt, 'DD.MM.YYYY') }}</span><span class="oa-detail">{{ receivedPart(item.createdAt, 'HH:mm') }} Uhr</span></td>
            <td class="oa-cell-status"><span class="oa-status" :class="'oa-status--' + item.status"><span aria-hidden="true"></span>{{ statusInfo(item.status).label }}</span></td>
            <td class="oa-cell-action" aria-hidden="true">
              <v-progress-circular v-if="openingAppointment === item.id" indeterminate color="#73dbac" size="20" width="2" />
              <v-icon v-else size="19" color="currentColor">{{ canOpenAppointment(item) ? 'mdi-open-in-new' : 'mdi-calendar-remove-outline' }}</v-icon>
            </td>
          </tr>
        </tbody>
      </table>
      <footer v-if="total && !loading && !error && !rangeError" class="oa-footer">
        <span>{{ firstItem }}–{{ lastItem }} von {{ total }} Buchungen <span class="oa-footer-time">· Schweizer Ortszeit</span></span>
        <v-pagination v-if="pageCount > 1" v-model="page" dark color="#347657" :length="pageCount" :total-visible="5" :disabled="Boolean(openingAppointment)" @input="load()" />
      </footer>
    </div>
  </section>
</template>

<script>
import moment from "moment-timezone"
import api from "@/api/shiftManagementApi"
import studioApi from "@/api"
import socketService from "@/socketService"

const statuses = {
  planned: { label: "Geplant" }, checkedin: { label: "Eingecheckt" }, done: { label: "Erledigt" },
  noshow: { label: "Nicht gekommen" }, cancelled: { label: "Abgesagt" }, deleted: { label: "Gelöscht" },
}

export default {
  name: "OnlineAppointments",
  data() {
    const today = moment.tz("Europe/Zurich")
    return {
      from: today.clone().subtract(29, "days").format("YYYY-MM-DD"),
      to: today.format("YYYY-MM-DD"), studioId: "", studios: [],
      items: [], total: 0, page: 1, pageSize: 25, loading: false, error: "", requestVersion: 0,
      openingAppointment: "", appointmentError: "",
      presets: [{ days: 1, label: "Heute" }, { days: 7, label: "7 Tage" }, { days: 30, label: "30 Tage" }],
    }
  },
  computed: {
    studioOptions() { return [{ id: "", name: "Alle Studios" }, ...this.studios] },
    rangeError() {
      const from = moment(this.from, "YYYY-MM-DD", true)
      const to = moment(this.to, "YYYY-MM-DD", true)
      return !from.isValid() || !to.isValid() || to.isBefore(from) || to.diff(from, "days") > 365
        ? "Bitte einen gültigen Zeitraum von höchstens 366 Tagen auswählen." : ""
    },
    emptyText() {
      if (this.error) return "Die Daten konnten nicht geladen werden. Bitte erneut aktualisieren."
      if (this.rangeError) return "Bitte den Zeitraum prüfen."
      return "In diesem Zeitraum sind keine Online-Buchungen eingegangen."
    },
    pageCount() { return Math.ceil(this.total / this.pageSize) },
    firstItem() { return (this.page - 1) * this.pageSize + 1 },
    lastItem() { return Math.min(this.page * this.pageSize, this.total) },
  },
  mounted() { this.load() },
  beforeDestroy() { this.requestVersion += 1 },
  methods: {
    statusInfo(status) { return statuses[status] || statuses.planned },
    datePart(value, format) { return value ? moment(value, "YYYY-MM-DD").locale("de-ch").format(format) : "–" },
    receivedPart(value, format) { return value ? moment(value).tz("Europe/Zurich").locale("de-ch").format(format) : "–" },
    isPreset(days) {
      const today = moment.tz("Europe/Zurich")
      return this.to === today.format("YYYY-MM-DD") && this.from === today.subtract(days - 1, "days").format("YYYY-MM-DD")
    },
    selectPreset(days) {
      const today = moment.tz("Europe/Zurich")
      this.to = today.format("YYYY-MM-DD")
      this.from = today.subtract(days - 1, "days").format("YYYY-MM-DD")
      this.applyFilters()
    },
    canOpenAppointment(item) { return Boolean(item && item.id && item.date && item.studioId && item.status !== "deleted") },
    appointmentLinkLabel(item) {
      return `${item.title || 'Termin'} am ${this.datePart(item.date, 'DD.MM.YYYY')} um ${item.start} in ${item.studioName} im Kalender öffnen (neuer Tab)`
    },
    async openAppointment(item) {
      if (!this.canOpenAppointment(item) || this.openingAppointment) return
      this.openingAppointment = item.id
      this.appointmentError = ""
      let tab = null
      try {
        // Match the existing calendar links: open synchronously, then change studio.
        tab = window.open("about:blank", "_blank")
        if (!tab) throw new Error("Bitte erlaube Pop-ups für Roody, um den Kalender zu öffnen.")
        tab.opener = null
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}")
        const currentStudioId = this.$store.state.studioStore.selectedStudio?._id || storedUser.user?.studio?._id
        if (String(currentStudioId || "") !== String(item.studioId)) {
          const studios = await studioApi.fetchAllowedAdminStudios()
          const studio = Array.isArray(studios) && studios.find((entry) => String(entry._id) === String(item.studioId))
          if (!studio?.code) throw new Error("Für das Studio dieses Termins besteht kein Kalenderzugriff.")
          const result = await studioApi.pickStudio({ studio_code: studio.code })
          if (String(result?.studio?._id || "") !== String(item.studioId)) throw new Error("Das Studio konnte nicht ausgewählt werden. Bitte erneut versuchen.")
          if (storedUser.user) {
            storedUser.user.studio_id = studio.code
            storedUser.user.studio = studio
            localStorage.setItem("user", JSON.stringify(storedUser))
          }
          await this.$store.dispatch("studioStore/setSelectedStudio", studio)
          if (socketService.socket?.connected) socketService.socket.emit("change_studio", { newStudioId: studio.code })
          await this.$store.dispatch("deviceStore/resetDeviceList")
          sessionStorage.removeItem("homeCourseData")
          sessionStorage.removeItem("homeNewsData")
        }
        if (tab.closed) throw new Error("Der neue Tab wurde geschlossen. Bitte öffne den Termin erneut.")
        const { href } = this.$router.resolve({ name: "workCalendar", query: { date: item.date, appointmentId: item.id } })
        tab.location.replace(href)
      } catch (error) {
        if (tab && !tab.closed) tab.close()
        this.appointmentError = error?.response?.data?.message || error.message || "Der Termin konnte nicht geöffnet werden."
      } finally {
        this.openingAppointment = ""
      }
    },
    applyFilters() { this.page = 1; this.load() },
    async load() {
      const version = ++this.requestVersion
      this.items = []
      this.total = 0
      this.error = ""
      this.appointmentError = ""
      if (this.rangeError) { this.loading = false; return }
      this.loading = true
      try {
        const result = await api.shiftManagement.fetchOnlineAppointments({ from: this.from, to: this.to, studioId: this.studioId, page: this.page })
        if (version !== this.requestVersion) return
        this.items = result.items
        this.total = result.total
        this.page = result.page
        this.pageSize = result.pageSize
        this.studios = result.studios
      } catch (error) {
        if (version !== this.requestVersion) return
        const status = error.response && error.response.status
        this.error = status === 401 || status === 403
          ? "Keine Berechtigung für diese Online-Termine."
          : "Online-Termine konnten nicht geladen werden. Bitte erneut versuchen."
      } finally {
        if (version === this.requestVersion) {
          this.loading = false
          this.$emit("finishedloading")
        }
      }
    },
  },
}
</script>

<style scoped>
.online-appointments {
  --oa-text: #f0f4f3;
  --oa-muted: #adb8b3;
  --oa-green: #82deb0;
  --oa-border: #3c4541;
  box-sizing: border-box;
  width: 100%; min-width: 0; max-width: 1480px;
  margin: 0 auto; padding: 28px clamp(12px, 2.2vw, 32px) 40px;
  color: var(--oa-text); color-scheme: dark;
}
.oa-header { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 26px; }
.oa-eyebrow { color: var(--oa-green); font-size: 11px; font-weight: 700; letter-spacing: .18em; text-transform: uppercase; margin: 0 0 8px; }
.oa-header h1 { color: var(--oa-text); font-size: clamp(26px, 2.3vw, 34px); line-height: 1.2; font-weight: 700; letter-spacing: -.035em; margin: 0; }
.oa-subtitle { color: var(--oa-muted); font-size: 14px; line-height: 1.6; margin: 10px 0 0; }
.oa-refresh { display: inline-flex; align-items: center; justify-content: center; gap: 9px; flex-shrink: 0; min-height: 40px; padding: 0 15px; border: 1px solid var(--oa-border); border-radius: 10px; color: var(--oa-text); background: #292f2c; font-size: 13px; font-weight: 600; }
.oa-refresh:hover:not(:disabled) { border-color: #7caa92; background: #313e36; }
.oa-refresh:disabled { opacity: .55; cursor: default; }
.oa-filters { padding: 18px 20px 20px; background: #272d2a; border: 1px solid var(--oa-border); border-radius: 16px; margin-bottom: 22px; }
.oa-filter-heading { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.oa-filter-heading > span { display: inline-flex; align-items: center; gap: 8px; color: var(--oa-text); font-size: 13px; font-weight: 600; }
.oa-presets { display: flex; gap: 4px; padding: 3px; border: 1px solid #39433d; border-radius: 9px; background: #1e2521; }
.oa-presets button { color: #bcc8c0; font-size: 12px; font-weight: 600; padding: 6px 13px; border-radius: 6px; min-height: 30px; }
.oa-presets button[aria-pressed="true"] { background: #354d40; color: #b2f1cd; }
.oa-presets button:hover:not(:disabled) { color: #fff; background: #35483d; }
.oa-filter-grid { display: grid; grid-template-columns: minmax(0, 1.5fr) repeat(2, minmax(0, 1fr)); gap: 16px; }
.oa-field { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.oa-field > span { font-size: 12px; font-weight: 500; color: var(--oa-muted); }
.oa-field input, .oa-field select { box-sizing: border-box; min-width: 0; width: 100%; height: 44px; padding: 0 12px; border: 1px solid #49544e; border-radius: 9px; color: #f0f4f3; background: #202622; font: inherit; font-size: 14px; }
.oa-field select { appearance: auto; padding-right: 10px; }
.oa-field input:focus, .oa-field select:focus { outline: 2px solid #82deb0; outline-offset: 2px; }
.oa-field input:disabled, .oa-field select:disabled { opacity: .6; }
.oa-notice { display: flex; align-items: center; gap: 10px; border: 1px solid #8b5656; border-radius: 12px; padding: 14px 18px; background: #422c2c; color: #ffd1cc; margin-bottom: 18px; font-size: 14px; }
.oa-error { color: #ffc2ba; margin: 14px 0 0; font-size: 13px; }
.oa-results { background: #242925; border: 1px solid var(--oa-border); border-radius: 16px; overflow: hidden; }
.oa-results-heading { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; padding: 20px; }
.oa-results-title { display: flex; align-items: center; gap: 10px; }
.oa-results-title h2 { color: var(--oa-text); font-size: 17px; line-height: 1.3; font-weight: 600; margin: 0; }
.oa-count { color: #b2f1cd; background: #354d40; padding: 3px 9px; min-width: 30px; text-align: center; border-radius: 7px; font-size: 12px; font-weight: 700; }
.oa-order { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; color: var(--oa-muted); }
.oa-table { width: 100%; border-collapse: collapse; table-layout: fixed; color: var(--oa-text); }
.oa-table th { text-align: left; background: #1f2521; color: #b7c3bc; font-size: 11px; font-weight: 600; padding: 13px 16px; border-block: 1px solid var(--oa-border); }
.oa-table th:nth-child(1) { width: 29%; }
.oa-table th:nth-child(2) { width: 23%; }
.oa-table th:nth-child(3) { width: 20%; }
.oa-table th:nth-child(4) { width: 13%; }
.oa-table th:nth-child(5) { width: 124px; }
.oa-table th:nth-child(6) { width: 40px; }
.oa-table td { padding: 18px 16px; vertical-align: middle; border-bottom: 1px solid #354039; font-size: 13px; overflow-wrap: anywhere; }
.oa-row:last-child td { border-bottom: 0; }
.oa-row--clickable { cursor: pointer; }
.oa-row--clickable:hover, .oa-row--clickable:focus-within { background: #2e3a32; }
.oa-appointment { display: flex; align-items: center; gap: 13px; width: 100%; text-align: left; color: inherit; border-radius: 7px; }
.oa-appointment:disabled { cursor: default; }
.oa-date-tile { flex: 0 0 46px; min-height: 56px; display: flex; align-items: center; justify-content: center; flex-direction: column; background: #33463a; border: 1px solid #47604e; border-radius: 10px; color: #c3f0d5; }
.oa-date-tile strong { font-size: 22px; line-height: 1.1; font-weight: 600; }
.oa-date-tile > span { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; margin-top: 3px; }
.oa-appointment-copy { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.oa-appointment-copy strong { font-size: 14px; font-weight: 600; color: #ecf7ef; line-height: 1.4; }
.oa-appointment-copy > span { color: var(--oa-muted); font-size: 12px; }
.oa-appointment-copy .oa-time { color: #c4e7d2; font-weight: 500; }
.oa-person-name { display: block; color: var(--oa-text); font-weight: 600; font-size: 13px; margin-bottom: 4px; }
.oa-detail { display: block; color: var(--oa-muted); font-size: 12px; line-height: 1.6; }
.oa-email { overflow-wrap: anywhere; }
.oa-studio-name { display: flex; align-items: center; gap: 4px; color: var(--oa-text); font-weight: 500; margin-bottom: 4px; }
.oa-cell-received { font-variant-numeric: tabular-nums; }
.oa-status { display: inline-flex; align-items: center; gap: 6px; padding: 5px 8px; border-radius: 7px; background: #343d37; color: #c6d6cc; font-size: 11px; font-weight: 600; line-height: 1.4; }
.oa-status > span { width: 5px; height: 5px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
.oa-status--planned { color: #a7eac2; background: #2c4436; }
.oa-status--checkedin { color: #a8d8f8; background: #293d4a; }
.oa-status--done { color: #b3dcce; background: #2d423a; }
.oa-status--noshow { color: #f4d6a0; background: #4b402d; }
.oa-status--cancelled { color: #f4b6b0; background: #4b3030; }
.oa-status--deleted { color: #b9c0bb; background: #363c38; }
.oa-table .oa-cell-action { padding: 18px 10px 18px 0; color: #86b99b; }
.oa-row--deleted .oa-date-tile { color: #b2bbb5; background: #313833; border-color: #465048; }
.oa-row--deleted .oa-appointment-copy strong { color: #bfc9c2; }
.oa-deleted-hint { display: block; color: #aab5ae; font-size: 10px; margin: 7px 0 0 59px; }
.oa-empty { display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 16px; min-height: 220px; padding: 36px 20px; color: var(--oa-muted); text-align: center; }
.oa-empty p { margin: 0; font-size: 14px; }
.oa-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 15px 20px; border-top: 1px solid var(--oa-border); font-size: 12px; color: var(--oa-muted); }
.oa-refresh:focus-visible, .oa-presets button:focus-visible, .oa-appointment:focus-visible { outline: 2px solid #82deb0; outline-offset: 4px; }
.oa-sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
@media (max-width: 1100px) {
  .oa-table td, .oa-table th { padding-inline: 12px; }
  .oa-table th:nth-child(1) { width: 28%; }
  .oa-table th:nth-child(2) { width: 22%; }
  .oa-table th:nth-child(3) { width: 18%; }
  .oa-table th:nth-child(4) { width: 15%; }
  .oa-appointment { gap: 9px; }
  .oa-date-tile { flex-basis: 40px; }
}
@media (max-width: 900px) {
  .oa-table, .oa-table tbody { display: block; }
  .oa-table thead { display: none; }
  .oa-table .oa-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; grid-template-areas: 'appointment action' 'person status' 'studio studio' 'received received'; gap: 13px 10px; padding: 20px; border-top: 1px solid var(--oa-border); }
  .oa-table td, .oa-table .oa-cell-action { display: block; border: 0; padding: 0; min-width: 0; }
  .oa-cell-appointment { grid-area: appointment; }
  .oa-cell-person { grid-area: person; }
  .oa-cell-studio { grid-area: studio; }
  .oa-cell-received { grid-area: received; font-size: 11px !important; color: var(--oa-muted); }
  .oa-cell-received::before { content: 'Eingegangen '; }
  .oa-cell-received .oa-detail { display: inline; margin-left: 6px; font-size: 11px; }
  .oa-cell-status { grid-area: status; align-self: start; }
  .oa-cell-action { grid-area: action; align-self: center; }
  .oa-cell-studio .oa-studio-name { display: inline-flex; margin-right: 8px; }
  .oa-cell-studio .oa-detail { display: inline; }
}
@media (max-width: 600px) {
  .online-appointments { padding: 18px 8px 30px; }
  .oa-header { flex-wrap: wrap; gap: 16px; margin-bottom: 20px; }
  .oa-subtitle { max-width: 330px; }
  .oa-refresh { min-height: 38px; }
  .oa-filters { padding: 16px; }
  .oa-filter-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 10px; }
  .oa-field--studio { grid-column: 1 / -1; }
  .oa-filter-heading { align-items: flex-start; }
  .oa-presets button { padding-inline: 10px; }
  .oa-field input, .oa-field select { font-size: 16px; padding-inline: 8px; }
  .oa-results-heading { padding: 18px 16px; }
  .oa-order { font-size: 11px; }
  .oa-table .oa-row { padding: 18px 16px; }
  .oa-footer { padding: 14px 16px; }
  .oa-footer-time { display: block; margin-top: 5px; }
}
</style>
